export default class WhoWeAre {
  constructor(section) {
    this.section = section;
    this.slides = Array.from(section.querySelectorAll("[data-who-we-are-slide]"));
    this.infoItems = Array.from(section.querySelectorAll("[data-who-we-are-info]"));
    this.dots = Array.from(section.querySelectorAll("[data-who-we-are-dot]"));
    this.gestureSurfaces = [
      section.querySelector("[data-who-we-are-carousel]"),
      section.querySelector(".who-we-are__info"),
    ].filter(Boolean);
    this.activeIndex = this.slides.length > 1 ? 1 : 0;
    this.hasRevealed = false;
    this.isAnimating = false;

    if (!this.slides.length) {
      return;
    }

    this.init();
  }

  init() {
    this.applyState();

    this.section.querySelectorAll("[data-who-we-are-prev]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate(-1);
      });
    });

    this.section.querySelectorAll("[data-who-we-are-next]").forEach((button) => {
      button.addEventListener("click", (event) => {
        event.preventDefault();
        button.blur();
        this.reveal();
        this.navigate(1);
      });
    });

    this.slides.forEach((slide, index) => {
      const activateSlide = () => {
        if (slide.dataset.whoWeAreSlot === "hidden") {
          return;
        }

        this.reveal();

        if (index === this.activeIndex) {
          this.navigate(1);
          return;
        }

        this.setActive(index);
      };

      slide.addEventListener("click", activateSlide);
      slide.addEventListener("keydown", (event) => {
        if (event.key !== "Enter" && event.key !== " ") {
          return;
        }

        event.preventDefault();
        activateSlide();
      });
    });

    this.dots.forEach((dot, index) => {
      dot.addEventListener("click", () => {
        this.reveal();
        this.setActive(index);
      });
    });

    this.initSwipeNavigation();
    this.initInfoClickNavigation();
    this.observeEntrance();
  }

  initInfoClickNavigation() {
    const info = this.section.querySelector(".who-we-are__info");

    if (!info || this.slides.length < 2) {
      return;
    }

    info.addEventListener("click", (event) => {
      if (event.defaultPrevented || event.target.closest("a, button, input, textarea, select") || this.isAnimating) {
        return;
      }

      const selection = window.getSelection();

      if (selection && !selection.isCollapsed) {
        return;
      }

      this.reveal();
      this.navigate(1);
    });
  }

  initSwipeNavigation() {
    if (this.slides.length < 2 || !("PointerEvent" in window)) {
      return;
    }

    this.gestureSurfaces.forEach((surface) => this.bindSwipeSurface(surface));
  }

  bindSwipeSurface(surface) {
    const directionThreshold = 8;
    const swipeThreshold = 48;
    const velocityThreshold = 0.45;
    let gesture = null;
    let suppressNextClick = false;

    const resetGesture = () => {
      surface.classList.remove("is-dragging");
      gesture = null;
    };

    surface.addEventListener("dragstart", (event) => event.preventDefault());

    surface.addEventListener("pointerdown", (event) => {
      if (!event.isPrimary || (event.pointerType === "mouse" && event.button !== 0) || this.isAnimating) {
        return;
      }

      if (event.target.closest("a, button, input, textarea, select")) {
        return;
      }

      gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startTime: performance.now(),
        horizontal: null,
      };
    });

    surface.addEventListener("pointermove", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) {
        return;
      }

      const distanceX = event.clientX - gesture.startX;
      const distanceY = event.clientY - gesture.startY;

      if (gesture.horizontal === null) {
        if (Math.abs(distanceX) < directionThreshold && Math.abs(distanceY) < directionThreshold) {
          return;
        }

        gesture.horizontal = Math.abs(distanceX) > Math.abs(distanceY);
      }

      if (!gesture.horizontal) {
        return;
      }

      if (!surface.hasPointerCapture(event.pointerId)) {
        surface.setPointerCapture(event.pointerId);
      }

      event.preventDefault();
      suppressNextClick = true;
      surface.classList.add("is-dragging");
    });

    surface.addEventListener("pointerup", (event) => {
      if (!gesture || event.pointerId !== gesture.pointerId) {
        return;
      }

      const distanceX = event.clientX - gesture.startX;
      const elapsed = Math.max(performance.now() - gesture.startTime, 1);
      const isSwipe = gesture.horizontal && (
        Math.abs(distanceX) >= swipeThreshold ||
        (Math.abs(distanceX) >= directionThreshold * 2 && Math.abs(distanceX) / elapsed >= velocityThreshold)
      );

      if (surface.hasPointerCapture(event.pointerId)) {
        surface.releasePointerCapture(event.pointerId);
      }

      resetGesture();

      if (isSwipe) {
        this.reveal();
        this.navigate(distanceX < 0 ? 1 : -1);
      }

      window.setTimeout(() => {
        suppressNextClick = false;
      }, 0);
    });

    surface.addEventListener("pointercancel", () => {
      suppressNextClick = false;
      resetGesture();
    });

    surface.addEventListener("click", (event) => {
      if (!suppressNextClick) {
        return;
      }

      event.preventDefault();
      event.stopImmediatePropagation();
      suppressNextClick = false;
    }, true);
  }

  observeEntrance() {
    if (!("IntersectionObserver" in window)) {
      this.reveal();
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) {
            return;
          }

          this.reveal();
          observer.unobserve(this.section);
        });
      },
      {
        rootMargin: "0px 0px -35% 0px",
        threshold: 0.24,
      },
    );

    observer.observe(this.section);
  }

  reveal() {
    if (this.hasRevealed) {
      return;
    }

    this.hasRevealed = true;
    this.section.classList.add("is-ready");
  }

  navigate(step) {
    const total = this.slides.length;

    if (total < 2) {
      return;
    }

    this.setActive((this.activeIndex + step + total) % total, step);
  }

  setActive(index, direction = 0) {
    if (index === this.activeIndex || this.isAnimating) {
      return;
    }

    const previousRects = this.getVisibleSlideRects();
    const previousActiveIndex = this.activeIndex;

    if (!direction) {
      const total = this.slides.length;
      const forwardDistance = (index - this.activeIndex + total) % total;
      const backwardDistance = (this.activeIndex - index + total) % total;
      direction = forwardDistance <= backwardDistance ? 1 : -1;
    }

    this.activeIndex = index;
    this.applyState();
    this.animateSlides(previousRects, direction, previousActiveIndex);
  }

  getVisibleSlideRects() {
    return new Map(
      this.slides
        .filter((slide) => slide.dataset.whoWeAreSlot !== "hidden")
        .map((slide) => {
          const rect = slide.getBoundingClientRect();
          return rect.width === 0 && rect.height === 0 ? null : [slide, rect];
        })
        .filter(Boolean),
    );
  }

  animateSlides(previousRects, direction, previousActiveIndex) {
    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const duration = prefersReducedMotion ? 0 : 520;
    const easing = "cubic-bezier(0.22, 1, 0.36, 1)";
    const animations = [];

    this.isAnimating = true;
    this.section.classList.add("is-switching");

    // Ensure new active slide is always rendered on top during animation
    this.slides.forEach((slide) => {
      slide.style.zIndex = slide.dataset.whoWeAreSlot === "active" ? "3" : "1";
    });

    this.slides.forEach((slide) => {
      const previousRect = previousRects.get(slide);
      const rawNext = slide.dataset.whoWeAreSlot === "hidden" ? null : slide.getBoundingClientRect();
      const nextRect = rawNext && (rawNext.width > 0 || rawNext.height > 0) ? rawNext : null;
      const wasActive = Number(slide.dataset.whoWeAreSlide) === previousActiveIndex;
      const isNowActive = slide.dataset.whoWeAreSlot === "active";

      if (previousRect && nextRect) {
        const translateX = previousRect.left - nextRect.left;
        const translateY = previousRect.top - nextRect.top;
        const scaleX = previousRect.width / nextRect.width;
        const scaleY = previousRect.height / nextRect.height;

        animations.push(
          slide.animate(
            [
              { opacity: 1, transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})` },
              { opacity: wasActive && !isNowActive ? 0.72 : 1, transform: "translate3d(0, 0, 0) scale(1, 1)" },
            ],
            { duration, easing, fill: "both" },
          ),
        );
      } else if (nextRect) {
        // Slide was hidden, now becomes visible
        animations.push(
          slide.animate(
            [
              { opacity: 0, transform: `translate3d(${direction * 32}px, 0, 0) scale(0.96)` },
              { opacity: 1, transform: "translate3d(0, 0, 0) scale(1)" },
            ],
            { duration: duration * 0.86, easing, fill: "both" },
          ),
        );
      } else if (previousRect) {
        // Slide was visible, now becomes hidden — fade it out
        animations.push(
          slide.animate(
            [{ opacity: 1 }, { opacity: 0 }],
            { duration: duration * 0.45, easing: "ease", fill: "both" },
          ),
        );
      }
    });

    // Info panels: horizontal crossfade
    const infoDuration = prefersReducedMotion ? 0 : 340;
    const offset = 18 * direction;

    this.infoItems.forEach((item) => {
      const itemIndex = Number(item.dataset.whoWeAreInfo);

      if (itemIndex === previousActiveIndex) {
        animations.push(
          item.animate(
            [
              { opacity: 1, transform: "translate3d(0, 0, 0)" },
              { opacity: 0, transform: `translate3d(${-offset}px, 0, 0)` },
            ],
            { duration: infoDuration * 0.75, easing, fill: "both" },
          ),
        );
      } else if (itemIndex === this.activeIndex) {
        animations.push(
          item.animate(
            [
              { opacity: 0, transform: `translate3d(${offset}px, 0, 0)` },
              { opacity: 1, transform: "translate3d(0, 0, 0)" },
            ],
            { duration: infoDuration, easing, fill: "both", delay: infoDuration * 0.18 },
          ),
        );
      }
    });

    Promise.all(animations.map((animation) => animation.finished.catch(() => undefined))).finally(() => {
      this.isAnimating = false;
      this.section.classList.remove("is-switching");
      this.slides.forEach((slide) => {
        slide.style.zIndex = "";
      });
    });
  }


  getSlot(index) {
    const total = this.slides.length;
    const prevIndex = (this.activeIndex - 1 + total) % total;
    const nextIndex = (this.activeIndex + 1) % total;

    if (index === this.activeIndex) {
      return "active";
    }

    if (total > 1 && index === prevIndex) {
      return "prev";
    }

    if (total > 2 && index === nextIndex) {
      return "next";
    }

    return "hidden";
  }

  applyState() {
    this.slides.forEach((slide, index) => {
      const slot = this.getSlot(index);
      const isActive = slot === "active";
      const isVisible = slot !== "hidden";

      slide.dataset.whoWeAreSlot = slot;
      slide.classList.toggle("is-active", isActive);
      slide.setAttribute("aria-hidden", isVisible ? "false" : "true");
      slide.tabIndex = isVisible ? 0 : -1;
    });

    this.infoItems.forEach((item) => {
      const isActive = Number(item.dataset.whoWeAreInfo) === this.activeIndex;

      item.classList.toggle("is-active", isActive);
      item.setAttribute("aria-hidden", isActive ? "false" : "true");
    });

    this.dots.forEach((dot, index) => {
      const isActive = index === this.activeIndex;

      dot.classList.toggle("is-active", isActive);
      dot.setAttribute("aria-current", isActive ? "true" : "false");
    });
  }
}

document.querySelectorAll(".who-we-are").forEach((section) => {
  new WhoWeAre(section);
});
